import { CheckOutlined } from '@ant-design/icons'
import { Drawer } from '@/components/ui/Drawer'

export interface ThemeColor {
  value: string
  color: string
  label: string
}

export interface ThemeColorPickerProps {
  /**
   * Currently selected theme value
   */
  value: string

  /**
   * Callback when theme is selected
   */
  onChange: (value: string) => void

  /**
   * Available theme colors
   */
  colors?: ThemeColor[]

  /**
   * Whether the drawer is open
   */
  open: boolean

  /**
   * Callback to close the drawer
   */
  onClose: () => void

  /**
   * Drawer title
   * @default 'Theme Colour'
   */
  title?: string

  /**
   * Description text shown at the top
   */
  description?: string

  /**
   * Whether to show the preview section
   * @default true
   */
  showPreview?: boolean
}

/**
 * Default theme colors matching THEMES constant from Event model
 */
export const DEFAULT_THEME_COLORS: ThemeColor[] = [
  { value: 'black', color: '#000000', label: 'Black' },
  { value: 'grey', color: '#6B7280', label: 'Grey' },
  { value: 'slate', color: '#475569', label: 'Slate' },
  { value: 'darkblue', color: '#1E40AF', label: 'Dark Blue' },
  { value: 'blue', color: '#3B82F6', label: 'Blue' },
  { value: 'pink', color: '#EC4899', label: 'Pink' },
  { value: 'darkred', color: '#991B1B', label: 'Dark Red' },
  { value: 'red', color: '#EF4444', label: 'Red' },
  { value: 'darkorange', color: '#C2410C', label: 'Dark Orange' },
  { value: 'orange', color: '#F97316', label: 'Orange' },
  { value: 'darkgreen', color: '#166534', label: 'Dark Green' },
  { value: 'green', color: '#22C55E', label: 'Green' },
]

/**
 * ThemeColorPicker Component
 *
 * A drawer-based color picker for selecting theme colors with live preview.
 *
 * @example
 * const [theme, setTheme] = useState('blue')
 * const [open, setOpen] = useState(false)
 *
 * <ThemeColorPicker
 *   value={theme}
 *   onChange={setTheme}
 *   open={open}
 *   onClose={() => setOpen(false)}
 * />
 */
export function ThemeColorPicker({
  value,
  onChange,
  colors = DEFAULT_THEME_COLORS,
  open,
  onClose,
  title = 'Theme Colour',
  description = 'You may select from a default colour. Theme colour will be applied on the Event App and registration forms.',
  showPreview = true,
}: ThemeColorPickerProps) {
  const selectedTheme = colors.find((t) => t.value === value)

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      showFooter={false}
      width={400}
    >
      <div className="space-y-6">
        {description && <p className="text-gray-600">{description}</p>}

        {/* Color Grid */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Theme Colour
          </label>
          <div className="grid grid-cols-6 gap-3">
            {colors.map((theme) => (
              <button
                key={theme.value}
                type="button"
                onClick={() => onChange(theme.value)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  value === theme.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                }`}
                style={{ backgroundColor: theme.color }}
                title={theme.label}
                aria-label={`Select ${theme.label} theme`}
                aria-pressed={value === theme.value}
              >
                {value === theme.value && (
                  <CheckOutlined className="text-white" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Preview Section */}
        {showPreview && selectedTheme && (
          <div className="space-y-6 pt-6 border-t border-gray-200">
            <h3 className="font-medium">Preview</h3>

            {/* Primary Buttons Preview */}
            <div>
              <h4 className="text-sm font-medium mb-2">1. Primary Buttons</h4>
              <p className="text-xs text-gray-500 mb-3">
                Applied to all primary action buttons such as "Register" and "Submit".
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="px-6 py-2 rounded text-white font-medium"
                  style={{ backgroundColor: selectedTheme.color }}
                >
                  Solid
                </button>
                <button
                  type="button"
                  className="px-6 py-2 rounded font-medium border-2"
                  style={{ borderColor: selectedTheme.color, color: selectedTheme.color }}
                >
                  Outline
                </button>
              </div>
            </div>

            {/* Checkboxes & Radio Buttons Preview */}
            <div>
              <h4 className="text-sm font-medium mb-2">2. Checkboxes & Radio Buttons</h4>
              <p className="text-xs text-gray-500 mb-3">
                Checkboxes are used for selecting multiple options from a set of selections;
                radio buttons are used for selecting an option from a set of selection.
              </p>
              <div className="flex gap-4">
                <div
                  className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ backgroundColor: selectedTheme.color }}
                >
                  <CheckOutlined className="text-white text-xs" />
                </div>
                <div
                  className="w-5 h-5 rounded-full border-4 flex items-center justify-center"
                  style={{ borderColor: selectedTheme.color }}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: selectedTheme.color }}
                  />
                </div>
              </div>
            </div>

            {/* Tabs Preview */}
            <div>
              <h4 className="text-sm font-medium mb-2">3. Tabs</h4>
              <p className="text-xs text-gray-500 mb-3">
                Tabs apply in attendee app to separate different content and features.
              </p>
              <div className="flex gap-4 border-b border-gray-200">
                <span
                  className="pb-2 border-b-2 text-sm"
                  style={{ borderColor: selectedTheme.color, color: selectedTheme.color }}
                >
                  Tab 1
                </span>
                <span className="pb-2 text-sm text-gray-500">Tab 2</span>
                <span className="pb-2 text-sm text-gray-500">Tab 3</span>
              </div>
              <div className="flex gap-2 mt-3">
                <span
                  className="px-3 py-1 rounded text-white text-sm"
                  style={{ backgroundColor: selectedTheme.color }}
                >
                  Tab 1
                </span>
                <span className="px-3 py-1 rounded border border-gray-300 text-sm">Tab 2</span>
                <span className="px-3 py-1 rounded border border-gray-300 text-sm">Tab 3</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  )
}

export interface ThemeColorButtonProps {
  /**
   * Currently selected theme value
   */
  value: string

  /**
   * Click handler to open the picker
   */
  onClick: () => void

  /**
   * Available theme colors (used to find selected color info)
   */
  colors?: ThemeColor[]

  /**
   * Placeholder text when no theme selected
   * @default 'Select Colour'
   */
  placeholder?: string
}

/**
 * ThemeColorButton Component
 *
 * A button that displays the currently selected theme color and opens the picker.
 *
 * @example
 * <ThemeColorButton
 *   value={theme}
 *   onClick={() => setDrawerOpen(true)}
 * />
 */
export function ThemeColorButton({
  value,
  onClick,
  colors = DEFAULT_THEME_COLORS,
  placeholder = 'Select Colour',
}: ThemeColorButtonProps) {
  const selectedTheme = colors.find((t) => t.value === value)

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
    >
      {selectedTheme && (
        <span
          className="inline-block w-4 h-4 rounded mr-2"
          style={{ backgroundColor: selectedTheme.color }}
        />
      )}
      {selectedTheme ? selectedTheme.label : placeholder}
    </button>
  )
}
